import { justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import {
  SetAttributesGroup,
  GetFiltersSearchLocationsPayload,
  GetLocationsPayload,
  GetShedsSearchLocationsPayload,
  Locations,
  SelectedLocations,
  SetFilterSearchLocations,
  SetLocationAttributeDiscrepancyErrors,
  SetLocations,
  SetSelectedLocations,
  SetShedsSearchLocations,
  ShedManagementState,
  GetProductsPayload,
  SetSelectedProducts,
  Product,
  SetProductAttributeDiscrepancyErrors,
  SetLocationHeightTypes,
  SetCreateFiltersData,
  CreateLocationsByCsvPayload,
  SetCsvValidationErrors,
} from './ShedManagementTypes'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export const initialState: ShedManagementState = {
  attributesGroup: [],
  shedsSearchLocations: [],
  requestsSearchingFilters: 0,
  filtersSearchLocations: {
    streets: [],
    shelves: [],
    bins: [],
    heightTypes: [],
    isGondola: [],
    isFresh: [],
  },
  locations: [],
  selectedLocations: [],
  locationAttributeDiscrepancyErrors: [],
  products: [],
  selectedProducts: [],
  productAttributeDiscrepancyErrors: [],
  locationHeightTypes: [],
  createFiltersData: null,
  csvValidationErrors: [],
}

const getShedsSearchLocations = justForSideEffectWithPayload<GetShedsSearchLocationsPayload>()
const getFiltersSearchLocations = justForSideEffectWithPayload<GetFiltersSearchLocationsPayload>()
const getLocations = justForSideEffectWithPayload<GetLocationsPayload>()
const saveAttributesLocations = justForSideEffectWithPayload<SelectedLocations[]>()
const getAttributesGroup = justForSideEffectWithPayload()
const getProducts = justForSideEffectWithPayload<GetProductsPayload>()
const saveAttributesProducts = justForSideEffectWithPayload<Product[]>()
const getLocationHeightTypes = justForSideEffectWithPayload()
const getCreateFilters = justForSideEffectWithPayload()
const createLocationsByCsv = justForSideEffectWithPayload<CreateLocationsByCsvPayload>()

const setAttributesGroup = (
  state: ShedManagementState,
  action: PayloadAction<SetAttributesGroup>,
): ShedManagementState => ({
  ...state,
  attributesGroup: action.payload.attributesGroup,
})

const setLocationHeightTypes = (
  state: ShedManagementState,
  action: PayloadAction<SetLocationHeightTypes>,
): ShedManagementState => ({
  ...state,
  locationHeightTypes: action.payload.locationHeightTypes,
})

const setCreateFiltersData = (
  state: ShedManagementState,
  action: PayloadAction<SetCreateFiltersData>,
): ShedManagementState => ({
  ...state,
  createFiltersData: action.payload.createFiltersData,
})

const setCsvValidationErrors = (
  state: ShedManagementState,
  action: PayloadAction<SetCsvValidationErrors>,
): ShedManagementState => ({
  ...state,
  csvValidationErrors: action.payload.csvValidationErrors,
})

const setShedsSearchLocations = (
  state: ShedManagementState,
  action: PayloadAction<SetShedsSearchLocations>,
): ShedManagementState => ({
  ...state,
  shedsSearchLocations: action.payload.shedsSearchLocations,
})

const addRequestsSearchingFilters = (state: ShedManagementState) => ({
  ...state,
  requestsSearchingFilters: state.requestsSearchingFilters + 1,
})

const removeRequestsSearchingFilters = (state: ShedManagementState) => ({
  ...state,
  requestsSearchingFilters: state.requestsSearchingFilters - 1,
})

const clearFiltersSearchLocations = (state: ShedManagementState) => ({
  ...state,
  filtersSearchLocations: {
    ...state.filtersSearchLocations,
    streets: [],
    shelves: [],
    bins: [],
    heightTypes: [],
    isGondola: [],
  },
})

const setFilterSearchLocations = (
  state: ShedManagementState,
  action: PayloadAction<SetFilterSearchLocations>,
): ShedManagementState => ({
  ...state,
  filtersSearchLocations: action.payload.filterSearchLocations,
})

const setLocations = (
  state: ShedManagementState,
  action: PayloadAction<SetLocations>,
): ShedManagementState => ({
  ...state,
  locations: action.payload.locations,
})

const updateLocations = (
  state: ShedManagementState,
  action: PayloadAction<SelectedLocations[]>,
): ShedManagementState => {
  const updatedLocations: Locations[] = []

  state.locations.forEach((existingLocation) => {
    const selectedLocation = action.payload.find((location) => location.id === existingLocation.id)

    if (selectedLocation) {
      updatedLocations.push({
        ...existingLocation,
        isGondola: selectedLocation.isGondola,
        isFresh: selectedLocation.isFresh,
        idLocationHeightType: selectedLocation.idLocationHeightType!,
        locationAttribute: selectedLocation.locationAttribute,
      })
    } else {
      updatedLocations.push(existingLocation)
    }
  })

  return {
    ...state,
    locations: updatedLocations,
  }
}

const setSelectedLocations = (
  state: ShedManagementState,
  action: PayloadAction<SetSelectedLocations>,
): ShedManagementState => ({
  ...state,
  selectedLocations: action.payload.selectedLocations,
})

const setLocationAttributeDiscrepancyErrors = (
  state: ShedManagementState,
  action: PayloadAction<SetLocationAttributeDiscrepancyErrors>,
): ShedManagementState => ({
  ...state,
  locationAttributeDiscrepancyErrors: action.payload.locationAttributeDiscrepancyErrors,
})

const setProducts = (
  state: ShedManagementState,
  action: PayloadAction<Product[]>,
): ShedManagementState => {
  const products = action.payload.map((product) => ({
    ...product,
    allowHighStorage: product.allowHighStorage ?? false,
    fractioningTag: product.fractioningTag ?? false,
  }))

  return {
    ...state,
    products,
  }
}

const setSelectedProducts = (
  state: ShedManagementState,
  action: PayloadAction<SetSelectedProducts>,
): ShedManagementState => ({
  ...state,
  selectedProducts: action.payload.selectedProducts,
})

const setProductAttributeDiscrepancyErrors = (
  state: ShedManagementState,
  action: PayloadAction<SetProductAttributeDiscrepancyErrors>,
): ShedManagementState => ({
  ...state,
  productAttributeDiscrepancyErrors: action.payload.productAttributeDiscrepancyErrors,
})

const updateProducts = (
  state: ShedManagementState,
  action: PayloadAction<Product[]>,
): ShedManagementState => {
  const updatedProducts: Product[] = []

  state.products.forEach((existingProduct) => {
    const selectedProduct = action.payload.find((product) => product.idModel === existingProduct.idModel)

    if (selectedProduct) {
      updatedProducts.push({
        ...existingProduct,
        idAttributeType: selectedProduct.idAttributeType,
        idAttributeFunction: selectedProduct.idAttributeFunction,
        idAttributeStorage: selectedProduct.idAttributeStorage,
        allowHighStorage: selectedProduct.allowHighStorage,
        fractioningTag: selectedProduct.fractioningTag,
        dueDateType: selectedProduct.dueDateType,
      })
    } else {
      updatedProducts.push(existingProduct)
    }
  })

  return {
    ...state,
    products: updatedProducts,
  }
}

const clearState = (): ShedManagementState => initialState

const slice = createSlice({
  name: 'shedManagement',
  initialState,
  reducers: {
    getAttributesGroup,
    setAttributesGroup,
    getShedsSearchLocations,
    setShedsSearchLocations,
    getFiltersSearchLocations,
    setFilterSearchLocations,
    getLocations,
    setLocations,
    setSelectedLocations,
    saveAttributesLocations,
    updateLocations,
    setLocationAttributeDiscrepancyErrors,
    getProducts,
    setProducts,
    setSelectedProducts,
    updateProducts,
    saveAttributesProducts,
    setProductAttributeDiscrepancyErrors,
    getLocationHeightTypes,
    setLocationHeightTypes,
    getCreateFilters,
    setCreateFiltersData,
    setCsvValidationErrors,
    createLocationsByCsv,
    addRequestsSearchingFilters,
    removeRequestsSearchingFilters,
    clearFiltersSearchLocations,
    clearState,
  },
})

export default slice.reducer

export const { actions } = slice
